import { Controller } from '@hotwired/stimulus';

// The introduction to what a workflow is.
//
// It opens by itself the first time a given workflow is opened, and never again
// for that one — "each workflow", not "each user": someone who has understood
// the model on their first document does not need it on the second, but someone
// discovering the product on a colleague's workflow does.
//
// What has been seen lives in localStorage rather than in a column. It is an
// overlay, not data: losing it on a new browser shows the tutorial again, which
// costs one Escape, while a column would cost a migration and a write on every
// page load. The trigger on the creation page passes no identifier at all, so
// nothing opens on its own there — the link is the only way in.
// The builder is React and this overlay is its ERB sibling, so the rail asks for
// it by event rather than by call. The name is declared on the other side in
// workflows/core/events.ts, which is the registry of that boundary; the two
// literals have to agree.
const OPEN_REQUEST = 'workflows:open-tutorial';

export default class extends Controller {
  static targets = ['overlay', 'dialog', 'step', 'dot', 'previous', 'next', 'counter'];
  static values = { templateId: Number, total: Number };

  static STORE = 'doclift.workflow-tutorial.seen';

  connect() {
    this.index = 0;
    this.returnFocusTo = null;
    window.addEventListener(OPEN_REQUEST, this.#onOpenRequest);

    if (this.hasTemplateIdValue && !this.#seen()) this.open();
  }

  disconnect() {
    window.removeEventListener(OPEN_REQUEST, this.#onOpenRequest);
    document.removeEventListener('keydown', this.#onKeyDown);
  }

  open() {
    this.#reveal(0);
  }

  #reveal(index) {
    this.returnFocusTo = document.activeElement;
    this.overlayTarget.hidden = false;
    this.#remember();
    this.#show(index);
    this.dialogTarget.focus({ preventScroll: true });
    document.addEventListener('keydown', this.#onKeyDown);
  }

  // Asked for by name and not by rank: the caller wants a subject, and inserting
  // a step before it must not send the reader somewhere else.
  #onOpenRequest = (event) => {
    const step = event instanceof CustomEvent ? event.detail?.step : null;
    const found = this.stepTargets.findIndex((element) => element.dataset.step === step);

    this.#reveal(found === -1 ? 0 : found);
  };

  dismiss() {
    this.overlayTarget.hidden = true;
    document.removeEventListener('keydown', this.#onKeyDown);
    // Back where it left, as every overlay of this application does: an author
    // who opened it from a link lands on that link again.
    if (this.returnFocusTo instanceof HTMLElement) this.returnFocusTo.focus();
  }

  next() {
    if (this.index >= this.stepTargets.length - 1) return this.dismiss();

    this.#show(this.index + 1);
  }

  previous() {
    this.#show(this.index - 1);
  }

  jump(event) {
    this.#show(Number(event.currentTarget.dataset.rank));
  }

  #show(index) {
    const last = this.stepTargets.length - 1;
    this.index = Math.max(0, Math.min(index, last));

    this.stepTargets.forEach((step, rank) => { step.hidden = rank !== this.index; });
    this.dotTargets.forEach((dot, rank) => {
      dot.toggleAttribute('aria-current', rank === this.index);
      if (rank === this.index) dot.setAttribute('aria-current', 'step');
    });

    this.previousTarget.disabled = this.index === 0;
    this.nextTarget.textContent = this.#label(this.index === last ? 'start' : 'next');
    this.counterTarget.textContent = this.#counter();
  }

  #counter() {
    return this.#label('counter')
      .replace('%{rank}', String(this.index + 1))
      .replace('%{total}', String(this.stepTargets.length));
  }

  // The wording is already on the page, in the locale the server rendered: the
  // two labels the button alternates between ride on the element rather than
  // being written again in JavaScript, where they could only be written in one
  // language.
  #label(key) {
    return this.nextTarget.dataset[key] || '';
  }

  #onKeyDown = (event) => {
    if (event.key === 'Escape') this.dismiss();
    if (event.key === 'ArrowRight') this.next();
    if (event.key === 'ArrowLeft') this.previous();
  };

  #seen() {
    return this.#store().includes(this.templateIdValue);
  }

  #remember() {
    if (!this.hasTemplateIdValue || this.#seen()) return;

    const seen = [...this.#store(), this.templateIdValue];
    try {
      window.localStorage.setItem(this.constructor.STORE, JSON.stringify(seen));
    } catch {
      // A browser refusing storage is a browser that shows this again. It is a
      // tutorial: that is an acceptable outcome, and not one worth reporting.
    }
  }

  #store() {
    try {
      const raw = window.localStorage.getItem(this.constructor.STORE);
      const parsed = raw ? JSON.parse(raw) : [];

      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}
